import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import ApexChart from "react-apexcharts";
import { getCohortAnalysis } from "api/visaVitaService";
import { groupLabel } from "./PatientManagement";

const GROUP_COLORS = {
  VISAVITA_GLUCOSE: '#3182CE',
  VISAVITA_STRESS: '#E53E3E',
  VISAVITA_CORSANO: '#38A169',
};

const format = (value, digits = 1) =>
  value === null || value === undefined ? '–' : Number(value).toFixed(digits).replace(/\.0+$/, '');

const formatDate = (millis) => (millis ? new Date(millis).toLocaleDateString() : '–');

const mean = (values) => (values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null);

/**
 * Mean of the per-patient averages of each metric for every subgroup of the cohort, plus the patients without one.
 */
const subgroupBreakdown = (patients, metrics) => {
  const buckets = new Map();
  patients.forEach((patient) => {
    const key = patient.subgroup ? patient.subgroup.id : 'none';
    if (!buckets.has(key)) {
      buckets.set(key, { name: patient.subgroup ? patient.subgroup.name : 'No subgroup', patients: [] });
    }
    buckets.get(key).patients.push(patient);
  });
  return [...buckets.entries()]
    .sort(([a], [b]) => (a === 'none') - (b === 'none'))
    .map(([key, bucket]) => ({
      key,
      name: bucket.name,
      patientCount: bucket.patients.length,
      averageDays: mean(bucket.patients.map((p) => p.daysWithData || 0)),
      metrics: Object.fromEntries(metrics.map((metric) => [
        metric.key,
        mean(bucket.patients.map((p) => p.metrics?.[metric.key]?.avg).filter((v) => v !== null && v !== undefined)),
      ])),
    }));
};

/**
 * Cohort analysis of one patient group (optionally one subgroup): summary statistics of the per-patient metrics,
 * a per-patient comparison, the breakdown by subgroup and the patient table.
 */
const CohortAnalysis = ({ group, subgroupId, refreshKey }) => {
  const history = useHistory();
  const cardBg = useColorModeValue("white", "gray.800");
  const statBg = useColorModeValue("gray.50", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const rowHoverBg = useColorModeValue("purple.50", "gray.600");
  const chartTextColor = useColorModeValue('#2D3748', '#A0AEC0');
  const chartGridColor = useColorModeValue('#E2E8F0', '#4A5568');
  const chartTheme = useColorModeValue('light', 'dark');

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartMetric, setChartMetric] = useState(null);

  // Which cohort the loaded data belongs to, so another group's results are never shown while loading
  const scope = `${group}:${subgroupId || ''}`;
  const latestScope = useRef(scope);
  latestScope.current = scope;

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCohortAnalysis(group, subgroupId || null);
      // A slower response for a cohort that is no longer selected is dropped
      if (latestScope.current !== scope) return;
      setData({ ...result, scope });
      // Keep the charted metric when this cohort has data for it, otherwise show the first one that has data
      const withData = (result.metrics || []).filter((m) => m.patientsWithData > 0);
      setChartMetric((current) =>
        withData.some((m) => m.key === current) ? current : (withData[0] || result.metrics?.[0])?.key || null);
    } catch (e) {
      if (latestScope.current !== scope) return;
      setError(e.message);
      setData(null);
    } finally {
      if (latestScope.current === scope) setIsLoading(false);
    }
  };

  useEffect(() => {
    setData((current) => (current?.scope === scope ? current : null));
    load();
  }, [group, subgroupId, refreshKey]);

  const color = GROUP_COLORS[group] || '#805AD5';
  const title = `Cohort analysis · ${groupLabel(group)}${data?.subgroup ? ` · ${data.subgroup.name}` : ''}`;

  if (isLoading && !data) {
    return (
      <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
        <VStack py={12} spacing={3}>
          <Spinner size="lg" color="purple.500" />
          <Text fontSize="sm" color="gray.500">Computing cohort statistics…</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
        <VStack py={8} spacing={3}>
          <Text color="red.500" fontWeight="medium">Failed to load the cohort analysis</Text>
          <Text fontSize="xs" color="gray.500">{error}</Text>
          <Button size="sm" variant="outline" onClick={load}>Retry</Button>
        </VStack>
      </Box>
    );
  }

  if (!data) return null;

  const metrics = data.metrics || [];
  const patients = data.patients || [];
  const selectedMetric = metrics.find((m) => m.key === chartMetric);
  const chartPatients = selectedMetric
    ? patients
      .filter((p) => p.metrics?.[selectedMetric.key]?.avg !== undefined)
      .sort((a, b) => b.metrics[selectedMetric.key].avg - a.metrics[selectedMetric.key].avg)
    : [];
  const breakdown = !subgroupId && patients.some((p) => p.subgroup) ? subgroupBreakdown(patients, metrics) : [];

  return (
    <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
      <HStack justify="space-between" align="start" mb={5} wrap="wrap" rowGap={2}>
        <VStack align="start" spacing={1}>
          <Text fontSize="lg" fontWeight="bold" color={color}>{title}</Text>
          <Text fontSize="sm" color="gray.600">
            Each patient's measurements are averaged first; the cohort figures summarise those per-patient values.
          </Text>
        </VStack>
        <Button size="sm" variant="ghost" onClick={load} isLoading={isLoading}>Refresh</Button>
      </HStack>

      {/* Cohort size */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={6}>
        <Box p={4} bg={statBg} borderRadius="md">
          <Stat>
            <StatLabel color="gray.600">Patients</StatLabel>
            <StatNumber color={color}>{data.patientCount}</StatNumber>
            <StatHelpText mb={0}>{data.totals?.patientsWithData ?? 0} with measurements</StatHelpText>
          </Stat>
        </Box>
        <Box p={4} bg={statBg} borderRadius="md">
          <Stat>
            <StatLabel color="gray.600">Measurements</StatLabel>
            <StatNumber color={color}>{Number(data.totals?.measurements || 0).toLocaleString()}</StatNumber>
            <StatHelpText mb={0}>all types</StatHelpText>
          </Stat>
        </Box>
        <Box p={4} bg={statBg} borderRadius="md">
          <Stat>
            <StatLabel color="gray.600">Days with data</StatLabel>
            <StatNumber color={color}>{format(data.totals?.averageDaysPerPatient)}</StatNumber>
            <StatHelpText mb={0}>average per patient</StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      {patients.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={8}>No patients in this cohort.</Text>
      ) : (
        <VStack align="stretch" spacing={8}>
          {/* Metric summaries */}
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
            {metrics.map((metric) => (
              <Box
                key={metric.key}
                p={4}
                bg={statBg}
                borderRadius="md"
                borderTop="3px solid"
                borderColor={metric.key === chartMetric ? color : 'transparent'}
                cursor="pointer"
                onClick={() => setChartMetric(metric.key)}
              >
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">{metric.label}</Text>
                {metric.patientsWithData > 0 ? (
                  <>
                    <Text fontSize="2xl" fontWeight="bold">
                      {format(metric.mean)}
                      {metric.sd !== null && metric.sd !== undefined && (
                        <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500"> ± {format(metric.sd)}</Text>
                      )}
                      <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500"> {metric.unit}</Text>
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Median {format(metric.median)}
                      {!metric.avgOnly && ` · Range ${format(metric.min)} – ${format(metric.max)}`}
                    </Text>
                    <Text fontSize="xs" color="gray.400">{metric.patientsWithData} of {patients.length} patients</Text>
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.400" mt={1}>No data</Text>
                )}
              </Box>
            ))}
          </SimpleGrid>

          {/* Per-patient comparison */}
          {selectedMetric && (
            <Box>
              <HStack justify="space-between" mb={2} wrap="wrap" rowGap={2}>
                <Text fontWeight="semibold">Per-patient average</Text>
                <Select size="sm" width="260px" value={chartMetric || ''} onChange={(e) => setChartMetric(e.target.value)}>
                  {metrics.map((metric) => (
                    <option key={metric.key} value={metric.key}>{metric.label}</option>
                  ))}
                </Select>
              </HStack>
              {chartPatients.length === 0 ? (
                <Text fontSize="sm" color="gray.400" py={6} textAlign="center">No patient has {selectedMetric.label.toLowerCase()} data.</Text>
              ) : (
                <ApexChart
                  type="bar"
                  height={Math.max(260, Math.min(chartPatients.length * 28, 700))}
                  series={[{
                    name: `${selectedMetric.label}${selectedMetric.unit ? ` (${selectedMetric.unit})` : ''}`,
                    data: chartPatients.map((p) => p.metrics[selectedMetric.key].avg),
                  }]}
                  options={{
                    chart: {
                      type: 'bar',
                      background: 'transparent',
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, { dataPointIndex }) => {
                          const patient = chartPatients[dataPointIndex];
                          if (patient) history.push(`/admin/patient-details/${patient.identifier}`);
                        },
                      },
                    },
                    plotOptions: { bar: { horizontal: true, barHeight: '70%' } },
                    colors: [color],
                    dataLabels: { enabled: false },
                    xaxis: {
                      // Glucose identifiers all start with the group token; without it they stay readable
                      categories: chartPatients.map((p) =>
                        p.identifier.startsWith(`${group}_`) ? p.identifier.slice(group.length + 1) : p.identifier),
                      labels: { style: { colors: chartTextColor } },
                    },
                    yaxis: { labels: { maxWidth: 240, style: { colors: chartTextColor } } },
                    grid: { borderColor: chartGridColor },
                    theme: { mode: chartTheme },
                    annotations: selectedMetric.mean !== null ? {
                      xaxis: [{
                        x: selectedMetric.mean,
                        borderColor: '#805AD5',
                        strokeDashArray: 4,
                        label: { text: `Cohort mean ${format(selectedMetric.mean)}`, style: { color: '#fff', background: '#805AD5' } },
                      }],
                    } : {},
                    tooltip: {
                      y: { formatter: (val) => `${format(val, 2)} ${selectedMetric.unit}` },
                    },
                  }}
                />
              )}
            </Box>
          )}

          {/* Subgroup comparison */}
          {breakdown.length > 0 && (
            <Box>
              <Text fontWeight="semibold" mb={2}>By subgroup</Text>
              <TableContainer>
                <Table size="sm">
                  <Thead bg={headerBg}>
                    <Tr>
                      <Th>Subgroup</Th>
                      <Th isNumeric>Patients</Th>
                      <Th isNumeric>Days / patient</Th>
                      {metrics.map((metric) => (
                        <Th key={metric.key} isNumeric>{metric.label}{metric.unit ? ` (${metric.unit})` : ''}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {breakdown.map((row) => (
                      <Tr key={row.key}>
                        <Td fontWeight="medium">{row.name}</Td>
                        <Td isNumeric>{row.patientCount}</Td>
                        <Td isNumeric>{format(row.averageDays)}</Td>
                        {metrics.map((metric) => (
                          <Td key={metric.key} isNumeric>{format(row.metrics[metric.key])}</Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Patients */}
          <Box>
            <Text fontWeight="semibold" mb={2}>Patients</Text>
            <TableContainer maxH="480px" overflowY="auto">
              <Table size="sm">
                <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>Patient</Th>
                    <Th>Subgroup</Th>
                    {group === 'VISAVITA_GLUCOSE' && <Th>Sex / Age</Th>}
                    <Th isNumeric>Days</Th>
                    <Th isNumeric>Measurements</Th>
                    <Th>Period</Th>
                    {metrics.map((metric) => (
                      <Th key={metric.key} isNumeric>{metric.label}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {patients.map((patient) => (
                    <Tr
                      key={patient.identifier}
                      cursor="pointer"
                      _hover={{ bg: rowHoverBg }}
                      onClick={() => history.push(`/admin/patient-details/${patient.identifier}`)}
                    >
                      <Td fontFamily="mono" fontWeight="medium">{patient.identifier}</Td>
                      <Td>
                        {patient.subgroup
                          ? <Badge colorScheme="purple" variant="subtle">{patient.subgroup.name}</Badge>
                          : <Text as="span" color="gray.400">–</Text>}
                      </Td>
                      {group === 'VISAVITA_GLUCOSE' && (
                        <Td>{patient.sex || '–'} / {patient.age !== undefined && patient.age !== null ? format(patient.age, 0) : '–'}</Td>
                      )}
                      <Td isNumeric>{patient.daysWithData}</Td>
                      <Td isNumeric>{Number(patient.measurementCount || 0).toLocaleString()}</Td>
                      <Td whiteSpace="nowrap">{formatDate(patient.firstMeasurement)} – {formatDate(patient.lastMeasurement)}</Td>
                      {metrics.map((metric) => (
                        <Td key={metric.key} isNumeric>{format(patient.metrics?.[metric.key]?.avg)}</Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </VStack>
      )}
    </Box>
  );
};

export default CohortAnalysis;
